import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1444044205806-38f3ed106c10?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxtaW5pbWFsJTIwY2FsbSUyMHN1bnNldCUyMGxhbmRzY2FwZXxlbnwwfHx8fDE3ODQxMzI5MTF8MA&ixlib=rb-4.1.0&q=85";

const lines = ["Your gentle", "daily companion."];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.35 } },
};
const lineVar = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1] } },
};

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section ref={ref} className="relative pt-32 md:pt-40 pb-10" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.15 }}
          className="font-body text-xs sm:text-sm uppercase tracking-[0.25em] text-[#2B2B26]/60 mb-6"
        >
          Wellness Hub &middot; mylifeorg.tech
        </motion.p>

        <motion.h1
          variants={container}
          initial="hidden"
          animate="show"
          className="font-serif font-light tracking-tight text-[#2B2B26] text-5xl sm:text-6xl md:text-7xl lg:text-[6rem] leading-[1.02]"
          data-testid="hero-heading"
        >
          {lines.map((line, i) => (
            <span className="line-mask" key={i}>
              <motion.span variants={lineVar} className="block">
                {line}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <p className="max-w-md font-body text-lg sm:text-xl leading-relaxed text-[#2B2B26]/70 font-light">
            A calm space for your day, health, and plans — one quiet home that greets you
            warmly and looks after your wellbeing.
          </p>
          <div className="flex items-center gap-4">
            <button
              data-testid="hero-waitlist-btn"
              onClick={() => scrollTo("waitlist")}
              className="rounded-full bg-[#2B2B26] text-[#F8F7F1] px-8 py-4 font-body text-sm font-medium transition-all duration-300 hover:bg-[#2B2B26]/90 hover:scale-[1.03] active:scale-95"
            >
              Get early access
            </button>
            <button
              data-testid="hero-explore-btn"
              onClick={() => scrollTo("features")}
              className="rounded-full border border-[#2B2B26]/20 px-8 py-4 font-body text-sm font-medium text-[#2B2B26] transition-all duration-300 hover:bg-[#EDE8DF]"
            >
              Explore
            </button>
          </div>
        </motion.div>
      </div>

      {/* Parallax hero image */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-14">
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden rounded-[2rem] md:rounded-[2.5rem] h-[52vh] md:h-[68vh] border border-[#2B2B26]/5 shadow-[0_30px_60px_rgb(43,43,38,0.10)]"
        >
          <motion.img
            src={HERO_IMG}
            alt="Calm golden sunset over a still sea"
            style={{ y: imgY, scale: imgScale }}
            className="absolute inset-0 w-full h-[130%] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2B2B26]/25 via-transparent to-transparent" />

          {/* Floating glass greeting card */}
          <motion.div
            style={{ y: overlayY }}
            className="absolute bottom-6 left-6 md:bottom-10 md:left-10 max-w-sm"
          >
            <div
              data-testid="hero-greeting-card"
              className="backdrop-blur-xl bg-[#F8F7F1]/80 border border-white/40 rounded-[1.5rem] p-6 shadow-[0_20px_40px_rgb(43,43,38,0.12)]"
            >
              <p className="font-body text-[11px] uppercase tracking-[0.2em] text-[#1877F2] mb-2">
                Good evening
              </p>
              <p className="font-serif text-xl md:text-2xl leading-snug text-[#2B2B26]">
                “I hope the day has treated you kindly, and that tonight unfolds with ease.”
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="max-w-7xl mx-auto px-6 md:px-12 mt-6 flex items-center gap-2 text-[#2B2B26]/50 font-body text-xs uppercase tracking-[0.2em]"
      >
        <ArrowDown className="w-4 h-4 animate-bounce" strokeWidth={1.5} /> Scroll to begin
      </motion.div>
    </section>
  );
};

export default Hero;
